import React, { useContext } from 'react';
import AuthContext from '../../store/AuthContext';
import NotAuthorizedPage from '../NotAuthorizedPage';
import { useBuyerKnotTabs } from '../../hooks/useBuyerKnotTabs';
import { iamProductEnabled, BROKER_TSP_UNAVAILABLE_MSG } from '../../utils/productAccess';
import { isDealKnotsAllowedForRole } from '../../utils/knotTabAccess';

/**
 * Route guard for Trade / Trade Support Program pages (parity with OpenKnotsOrNotFound).
 * Brokers inherit the associated buyer org's BAAS (trade) flags only — TSP is disabled (APPLICATIO-2912).
 * Uses IAM GET flags only (fail-closed) — same source as transaction deep-link gate.
 *
 * @param {'trade'|'deal'|'any'} product - 'any' = at least one of trade/deal enabled
 */
const ProductAccessGuard = ({ product, children }) => {
    const ctx = useContext(AuthContext);
    const role = ctx.userData?.user_organizations?.[0]?.user_org_role_maps?.[0]?.platform_role;
    const associatedBuyer = ctx.associatedBuyer;

    const knotTabs = useBuyerKnotTabs(ctx.userData, {
        organizationUlid: role === 'broker' ? associatedBuyer : undefined,
    });

    if (role !== 'buyer' && role !== 'supplier' && role !== 'broker') {
        return children;
    }

    if (product === 'deal' && !isDealKnotsAllowedForRole(role)) {
        return (
            <NotAuthorizedPage
                title='403 — Not authorized'
                message={BROKER_TSP_UNAVAILABLE_MSG}
            />
        );
    }

    if (role === 'broker' && !associatedBuyer) {
        return (
            <NotAuthorizedPage
                title='Select a buyer'
                message='Select a buyer organization before accessing BAAS.'
            />
        );
    }

    if (!knotTabs.loaded) {
        return null;
    }

    const tradeEnabled = iamProductEnabled(knotTabs.flags, 'trade');
    const dealEnabled = iamProductEnabled(knotTabs.flags, 'deal');

    if (!tradeEnabled && !dealEnabled) {
        const noAccessMessage =
            role === 'broker'
                ? "You don't have access to BAAS for this organization. If you believe this is a mistake, please contact your administrator."
                : "You don't have access to BAAS or Trade Support Program for this organization. If you believe this is a mistake, please contact your administrator.";
        return (
            <NotAuthorizedPage
                title="You don't have access"
                message={noAccessMessage}
                showBackLink={false}
            />
        );
    }

    if (product === 'any') {
        return children;
    }

    const allowed = product === 'deal' ? dealEnabled : tradeEnabled;
    if (!allowed) {
        const label = product === 'deal' ? 'Trade Support Program' : 'BAAS';
        return (
            <NotAuthorizedPage
                title='403 — Not authorized'
                message={`${label} is not enabled for this organization. If you believe this is a mistake, contact your administrator.`}
            />
        );
    }

    return children;
};

export default ProductAccessGuard;
